import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';

/** Export statuses of an export planning record that mean the export will never finish. */
const FAILED_EXPORT_STATUSES = ['ERROR', 'FAILED'];
/** Statuses of the export job that mean the job will never produce an export. */
const FAILED_JOB_STATUSES = ['ERROR', 'FAILED'];
const FINISHED_JOB_STATUSES = ['DONE', ...FAILED_JOB_STATUSES];

export interface BookingGroup {
  booking_Group_ID: string;
  bookingGroup_Name: string;
}

export interface JournalProcessDefinition {
  journalProcessDefinition_ID: string;
  booking_Group_ID: string;
  name: string;
}

export interface ExportDefinition {
  exportDefinition_ID: string;
  exportDefinition_Name: string;
}

export interface CreateExportForGeneralledgerRequest {
  importRunId: string;
  bookingGroupId: string;
  journalProcessDefinitionId: string;
  exportDefinitionId: string;
  sendTo: string;
}

export interface ExportForGeneralledger {
  journalPlanningRecordId: string;
  sequence: number;
  journalProcessDefinitionId: string;
  exportDefinitionId: string;
  jobProcessId: string;
  institutionId: string;
  payrollYear: number;
  payrollPeriod: number;
  referenceDate: string;
}

export type ExportProgressStatus = 'PENDING' | 'DONE' | 'FAILED';

/** Combined progress of the export job and the export planning record it creates. */
export interface ExportProgress {
  status: ExportProgressStatus;
  fileName: string;
  errorMessage: string | null;
}

export interface ExportDownload {
  exportPlanningRecordId: string;
  exportDefinitionId: string;
  journalPlanningRecordId: string;
  institutionId: string;
  bookingGroupId: string;
  exportDefinitionName: string;
  journalPlanningRecordName: string;
  institutionName: string;
  bookingGroupName: string;
  fileName: string;
  exportFileExtensionCode: string;
  bookingYear: number;
  bookingPeriod: number;
  bookingDate: string;
  changeDateTime: string;
  exportStatus: string;
  errorMessage: string;
  sendTo: string;
}

export interface ExportPlanningRecordDetails {
  export_PlanningRecord_ID: string;
  export_Status: string | null;
  errorMessage: string | null;
  fileName: string | null;
}

/** Subset of the job process that tells whether the export job itself failed. */
interface JobProcessStatusDetails {
  status: string | null;
  errorMessage: string | null;
}

export interface ImportRunDetails {
  importRunId: string;
  institutionId: string;
  institutionName: string;
  ceanr: number;
  year: number;
  period: number;
  payrollRunNumber: number;
  payrollSequence: number;
  importStatus: string;
  importDatetime: string;
  deleteDate: string;
  toBeDeleted: boolean;
  changeUser: string;
  changeDateTime: string;
}

@Injectable({
  providedIn: 'root',
})
export class PayrolljournalService {
  private apiUrl = environment.apiUrl + '/kbj-frontend/api/v1';
  private readonly httpOptions = { withCredentials: true };
  private http: HttpClient = inject(HttpClient);

  /**
   * Returns the booking groups (boekingsgroepen) available for the current context.
   */
  getBookingGroups(): Observable<BookingGroup[]> {
    return this.http
      .get<BookingGroup[] | null>(this.apiUrl + '/BookingGroup/ByContext', this.httpOptions)
      .pipe(
        map((jsonData) => jsonData ?? []),
        catchError((err: HttpErrorResponse) => this.handleListError<BookingGroup>(err)),
      );
  }

  /**
   * Returns the journal process definitions (verwerkingsdefinities) for a booking group.
   */
  getJournalProcessDefinitions(bookingGroupId: string): Observable<JournalProcessDefinition[]> {
    if (!bookingGroupId) {
      return of([]);
    }

    return this.http
      .get<JournalProcessDefinition[] | null>(
        `${this.apiUrl}/JournalProcessDefinition/ByContext/${bookingGroupId}`,
        this.httpOptions,
      )
      .pipe(
        map((jsonData) => jsonData ?? []),
        catchError((err: HttpErrorResponse) => this.handleListError<JournalProcessDefinition>(err)),
      );
  }

  /**
   * Returns the export definitions (exportdefinities) available for the current context.
   */
  getExportDefinitions(): Observable<ExportDefinition[]> {
    return this.http
      .get<ExportDefinition[] | null>(this.apiUrl + '/ExportDefinition/ByContext', this.httpOptions)
      .pipe(
        map((jsonData) => jsonData ?? []),
        catchError((err: HttpErrorResponse) => this.handleListError<ExportDefinition>(err)),
      );
  }

  /**
   * Returns the import run (loonjournaal) identified by its primary key.
   */
  getImportRun(importRunId: string): Observable<ImportRunDetails | null> {
    if (!importRunId) {
      return of(null);
    }

    return this.http
      .get<ImportRunDetails | null>(
        `${this.apiUrl}/ImportRun/${importRunId}`,
        this.httpOptions,
      )
      .pipe(
        map((jsonData) => jsonData ?? null),
        catchError((err: HttpErrorResponse) => this.handleError(err)),
      );
  }

  /**
   * Creates a journal planning record and queues the general ledger export job.
   */
  createExportForGeneralledger(
    request: CreateExportForGeneralledgerRequest,
  ): Observable<ExportForGeneralledger> {
    return this.http
      .post<ExportForGeneralledger>(
        `${this.apiUrl}/Processing/CreateExportForGeneralledger`,
        request,
        this.httpOptions,
      )
      .pipe(catchError((err: HttpErrorResponse) => this.handleError(err)));
  }

  /**
   * Returns the progress of a queued general ledger export.
   *
   * The export planning record carries the final export status and error message, but it is
   * only created part way through the job. The job process is therefore read first, so a job
   * that failed (or finished without ever creating an export planning record) is reported as
   * a failure instead of being polled forever.
   */
  getExportProgress(
    journalPlanningRecordId: string,
    jobProcessId: string,
  ): Observable<ExportProgress> {
    return this.getJobProcessStatus(jobProcessId).pipe(
      switchMap((job) =>
        this.getExportPlanningRecordByJournalPlanningRecord(journalPlanningRecordId).pipe(
          map((record) => this.toExportProgress(job, record)),
        ),
      ),
    );
  }

  private toExportProgress(
    job: JobProcessStatusDetails | null,
    record: ExportPlanningRecordDetails | null,
  ): ExportProgress {
    const fileName = record?.fileName?.trim() || '';

    if (record) {
      const exportStatus = record.export_Status?.trim().toUpperCase() ?? '';
      if (exportStatus === 'DONE') {
        return { status: 'DONE', fileName, errorMessage: null };
      }
      if (FAILED_EXPORT_STATUSES.includes(exportStatus)) {
        return { status: 'FAILED', fileName, errorMessage: record.errorMessage?.trim() || null };
      }
      return { status: 'PENDING', fileName, errorMessage: null };
    }

    // No export planning record (yet): only the job can tell whether it is still coming.
    const jobStatus = job?.status?.trim().toUpperCase() ?? '';
    if (FAILED_JOB_STATUSES.includes(jobStatus)) {
      return { status: 'FAILED', fileName, errorMessage: job?.errorMessage?.trim() || null };
    }
    if (FINISHED_JOB_STATUSES.includes(jobStatus)) {
      // The job ran to completion before the export planning record was read, so it will never appear.
      return { status: 'FAILED', fileName, errorMessage: null };
    }
    return { status: 'PENDING', fileName, errorMessage: null };
  }

  private getJobProcessStatus(jobProcessId: string): Observable<JobProcessStatusDetails | null> {
    if (!jobProcessId) {
      return of(null);
    }

    return this.http
      .get<JobProcessStatusDetails | null>(
        `${environment.apiUrl}/kbj-frontend/api/v1/Master/JobProcess/${jobProcessId}`,
        this.noCacheOptions(),
      )
      .pipe(
        map((jsonData) => jsonData ?? null),
        catchError((err: HttpErrorResponse) => {
          console.error(
            `Loading job process ${jobProcessId} failed: ${err.status} ${err.message}`,
          );
          return of(null);
        }),
      );
  }

  /** Options for polled requests, whose responses must never be served from cache. */
  private noCacheOptions() {
    return {
      ...this.httpOptions,
      headers: new HttpHeaders({
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        Pragma: 'no-cache',
      }),
      params: { _: Date.now().toString() },
    };
  }

  /**
   * Returns the previously created general ledger exports for an institution and booking period.
   */
  getExportDownloadList(
    institutionId: string,
    bookingYear: number,
    bookingPeriod: number,
  ): Observable<ExportDownload[]> {
    return this.http
      .get<ExportDownload[] | null>(
        `${this.apiUrl}/Processing/ExportGeneralLedger/DownloadList/${institutionId}/${bookingYear}/${bookingPeriod}`,
        this.httpOptions,
      )
      .pipe(
        map((jsonData) => jsonData ?? []),
        catchError((err: HttpErrorResponse) => this.handleListError<ExportDownload>(err)),
      );
  }

  /**
   * Returns the export planning record, which holds the extended error message of a failed export.
   */
  getExportPlanningRecordByJournalPlanningRecord(
    journalPlanningRecordId: string,
  ): Observable<ExportPlanningRecordDetails | null> {
    if (!journalPlanningRecordId) {
      return of(null);
    }

    return this.http
      .get<ExportPlanningRecordDetails | null>(
        `${this.apiUrl}/ExportPlanningRecord/ByJournalPlanningRecord/${journalPlanningRecordId}`,
        this.noCacheOptions(),
      )
      .pipe(
        map((jsonData) => jsonData ?? null),
        catchError((err: HttpErrorResponse) => {
          console.error(
            `Loading export planning record for journal planning record ${journalPlanningRecordId} failed: ${err.status} ${err.message}`,
          );
          return of(null);
        }),
      );
  }

  /**
   * Downloads the export file of the journal planning record and triggers a browser download.
   */
  downloadExportFile(journalPlanningRecordId: string, fallbackFileName: string): Observable<void> {
    return this.http
      .get(
        `${this.apiUrl}/Processing/JournalPlanningRecord/${journalPlanningRecordId}/ExportFile`,
        { ...this.httpOptions, observe: 'response', responseType: 'blob' },
      )
      .pipe(
        map((response: HttpResponse<Blob>) => this.triggerDownload(response, fallbackFileName)),
        catchError((err: HttpErrorResponse) => this.handleError(err)),
      );
  }

  private triggerDownload(response: HttpResponse<Blob>, fallbackFileName: string): void {
    const blob = response.body;
    if (!blob || blob.size === 0) {
      throw new Error('Export file is empty.');
    }

    const fileName =
      this.resolveFileName(response.headers.get('content-disposition')) ?? fallbackFileName;
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    window.URL.revokeObjectURL(url);
  }

  private resolveFileName(contentDisposition: string | null): string | null {
    if (!contentDisposition) {
      return null;
    }

    const utf8Match = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition);
    if (utf8Match?.[1]) {
      return decodeURIComponent(utf8Match[1].trim());
    }

    const asciiMatch = /filename="?([^";]+)"?/i.exec(contentDisposition);
    return asciiMatch?.[1]?.trim() || null;
  }

  private handleListError<T>(err: HttpErrorResponse): Observable<T[]> {
    if (err.status === 404 || err.status === 204) {
      return of([]);
    }
    return this.handleError(err);
  }

  private handleError(err: HttpErrorResponse) {
    const errorMessage = `An error has occurred: ${err.error?.message ?? err.message}`;
    console.error(errorMessage);
    return throwError(() => errorMessage);
  }
}
